import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, DollarSign, CreditCard, AlertCircle, Send, Settings, Download } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { InvoiceSettingsService, InvoiceSettings, defaultInvoiceSettings } from '../services/invoiceSettingsService';
import { useBusiness } from '../contexts/BusinessContext';
import RichTextDescription from './RichTextDescription';
import SendInvoiceModal from './SendInvoiceModal';
import { sendGmailEmail } from '../lib/email';

interface ProductPaymentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  productSale: any; // Product sale object with payments
  onUpdate?: () => void;
}

export default function ProductPaymentsModal({ 
  isOpen, 
  onClose, 
  productSale,
  onUpdate
}: ProductPaymentsModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaymentHistoryOpen, setIsPaymentHistoryOpen] = useState(false);
  const [invoiceSettings, setInvoiceSettings] = useState<InvoiceSettings | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSendInvoiceModalOpen, setIsSendInvoiceModalOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);

  // Calculate payment status
  const totalAmount = productSale?.total_amount || 0;
  const completedPayments = productSale?.payments?.filter((p: any) => p.status === 'completed') || [];
  
  // Calculate base payments (excluding tips)
  const totalPaid = completedPayments.reduce((sum: number, payment: any) => sum + (payment.base_amount || payment.amount || 0), 0);
  
  // Calculate total tips received
  const totalTips = completedPayments.reduce((sum: number, payment: any) => sum + (payment.tip_amount || 0), 0);
  
  const balanceOwing = Math.max(0, totalAmount - totalPaid);
  
  const paymentStatus = balanceOwing === 0 ? 'paid' : totalPaid > 0 ? 'partial' : 'unpaid';

  // Fetch invoice settings when modal opens
  useEffect(() => {
    const fetchInvoiceSettings = async () => {
      if (!isOpen || !selectedBusiness?.id) return;
      
      setIsLoadingSettings(true);
      try {
        const settings = await InvoiceSettingsService.getInvoiceSettings(selectedBusiness.id);
        if (settings) {
          setInvoiceSettings(settings);
        } else {
          // No settings found, create default settings with business info
          const defaultSettings: InvoiceSettings = {
            business_id: selectedBusiness.id,
            company_name: selectedBusiness.name || 'Your Business Name',
            company_address: selectedBusiness.address || '123 Business Street\nCity, State 12345',
            company_phone: selectedBusiness.phone || '(555) 123-4567',
            company_email: selectedBusiness.email || 'info@yourbusiness.com',
            company_website: `www.${selectedBusiness.name?.toLowerCase().replace(/\s+/g, '')}.com` || 'www.yourbusiness.com',
            ...defaultInvoiceSettings
          } as InvoiceSettings;
          setInvoiceSettings(defaultSettings);
        }
      } catch (error) {
        console.error('Error fetching invoice settings:', error);
        // Fall back to defaults
        const fallbackSettings: InvoiceSettings = {
          business_id: selectedBusiness.id,
          company_name: selectedBusiness.name || 'Your Business Name',
          company_address: selectedBusiness.address || '123 Business Street\nCity, State 12345',
          company_phone: selectedBusiness.phone || '(555) 123-4567',
          company_email: selectedBusiness.email || 'info@yourbusiness.com',
          company_website: `www.${selectedBusiness.name?.toLowerCase().replace(/\s+/g, '')}.com` || 'www.yourbusiness.com',
          ...defaultInvoiceSettings
        } as InvoiceSettings;
        setInvoiceSettings(fallbackSettings);
      } finally {
        setIsLoadingSettings(false);
      }
    };

    fetchInvoiceSettings();
  }, [isOpen, selectedBusiness?.id]);

  const handleDownloadPDF = async () => {
    if (isDownloadingPDF || !productSale?.id) return;
    setIsDownloadingPDF(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-invoice-pdf', {
        body: { productSaleId: productSale.id }
      });
      if (error || !data?.base64) {
        alert('Failed to generate PDF. Please try again.');
        return;
      }
      const bin = atob(data.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });

      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const file = new File([blob], data.filename, { type: 'application/pdf' });
        if (typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: data.filename }).catch(() => {});
        }
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || `product-sale-${productSale.sale_number}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('PDF download error:', err);
      alert('Failed to download PDF.');
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <>
      {/* Backdrop with blur */}
      <div 
        className={`fixed inset-0 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          zIndex: 100000,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
        onClick={onClose}
        onTransitionEnd={handleTransitionEnd}
      />

      {/* Modal Panel — full-screen on mobile, right-side on desktop */}
      <div 
        className={`fixed bg-white transform transition-transform duration-300 ease-out inset-0 sm:inset-y-0 sm:left-auto sm:right-0 sm:w-[672px] ${
          isOpen ? 'translate-y-0 sm:translate-y-0 sm:translate-x-0' : 'translate-y-full sm:translate-y-0 sm:translate-x-full'
        }`}
        style={{
          zIndex: 100001,
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header with iOS safe area */}
          <div 
            className="bg-white border-b border-gray-100 flex items-center justify-between shrink-0"
            style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))', paddingBottom: '0.75rem', paddingLeft: '1.25rem', paddingRight: '1.25rem' }}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gray-100 sm:hidden">
                <DollarSign className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <h2 className="text-[17px] font-semibold text-gray-900 leading-tight">Invoice</h2>
                <p className="text-xs text-gray-500 mt-0.5">Sale #{productSale?.sale_number || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownloadPDF}
                disabled={isDownloadingPDF}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors active:bg-blue-100 active:scale-95 touch-manipulation disabled:opacity-50"
                style={{ WebkitTapHighlightColor: 'transparent' }}
                title="Download PDF"
              >
                {isDownloadingPDF ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-blue-600" />
                ) : (
                  <Download className="h-5 w-5" />
                )}
              </button>
              <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                paymentStatus === 'paid' 
                  ? 'bg-green-100 text-green-800' 
                  : paymentStatus === 'partial' 
                  ? 'bg-yellow-100 text-yellow-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {paymentStatus === 'paid' ? 'Paid' : paymentStatus === 'partial' ? 'Partial' : 'Unpaid'}
              </div>
              <button
                onClick={onClose}
                className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors active:bg-gray-200 active:scale-95 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="space-y-0">
              
              {/* Payment Summary Section - Identical to BetaPaymentsModal */}
              <div className="bg-white border-b border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Payment Summary</h3>

                <div className="grid grid-cols-4 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
                  {/* Total Amount */}
                  <div className="text-center p-2 md:p-4 bg-gray-50 rounded-lg">
                    <div className="text-lg md:text-2xl font-bold text-gray-900">
                      ${totalAmount.toLocaleString()}
                    </div>
                    <div className="text-xs md:text-sm text-gray-500 mt-1">Total</div>
                  </div>

                  {/* Amount Paid */}
                  <div className="text-center p-2 md:p-4 bg-green-50 rounded-lg">
                    <div className="text-lg md:text-2xl font-bold text-green-700">
                      ${totalPaid.toLocaleString()}
                    </div>
                    <div className="text-xs md:text-sm text-gray-500 mt-1">Paid</div>
                  </div>

                  {/* Tips Received - Product sales don't have tips, but keep structure consistent */}
                  {totalTips > 0 && (
                    <div className="text-center p-2 md:p-4 bg-yellow-50 rounded-lg">
                      <div className="text-lg md:text-2xl font-bold text-yellow-700">
                        ${totalTips.toLocaleString()}
                      </div>
                      <div className="text-xs md:text-sm text-gray-500 mt-1">Tips</div>
                    </div>
                  )}

                  {/* Balance Owing */}
                  <div className="text-center p-2 md:p-4 bg-red-50 rounded-lg">
                    <div className="text-lg md:text-2xl font-bold text-red-700">
                      ${balanceOwing.toLocaleString()}
                    </div>
                    <div className="text-xs md:text-sm text-gray-500 mt-1">Balance</div>
                  </div>
                </div>

                {/* Payment History Dropdown - Identical to BetaPaymentsModal */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setIsPaymentHistoryOpen(!isPaymentHistoryOpen)}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-700">Payment History</span>
                      <span className="text-xs text-gray-500">({(productSale?.payments || []).length} entries)</span>
                    </div>
                    <div className={`transform transition-transform duration-200 ${isPaymentHistoryOpen ? 'rotate-180' : ''}`}>
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  
                  {isPaymentHistoryOpen && (
                    <div className="mt-4 space-y-3">
                      {(() => {
                        const payments = productSale?.payments || [];
                        
                        // Sort payments by created_at (newest first)
                        const sortedPayments = [...payments].sort((a: any, b: any) => 
                          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                        );
                        
                        if (sortedPayments.length === 0) {
                          return (
                            <div className="p-4 rounded-lg border backdrop-blur-sm shadow-sm bg-gray-50 border-gray-200">
                              <div className="text-center text-gray-500">
                                <div className="text-sm font-medium">No payment entries yet</div>
                                <div className="text-xs mt-1">Payment records will appear here when created</div>
                              </div>
                            </div>
                          );
                        }
                        
                        return sortedPayments.map((payment: any, index: number) => {
                          // Determine styling based on payment status
                          let statusConfig = {
                            color: 'text-gray-600',
                            bgColor: 'bg-gray-50',
                            borderColor: 'border-gray-200',
                            label: payment.status || 'Unknown',
                            icon: '●'
                          };
                          
                          switch (payment.status) {
                            case 'completed':
                            case 'succeeded':
                            case 'paid':
                              statusConfig = {
                                color: 'text-green-600',
                                bgColor: 'bg-green-50',
                                borderColor: 'border-green-200',
                                label: 'Completed',
                                icon: '✓'
                              };
                              break;
                            case 'pending':
                              statusConfig = {
                                color: 'text-yellow-600',
                                bgColor: 'bg-yellow-50',
                                borderColor: 'border-yellow-200',
                                label: 'Pending',
                                icon: '⏳'
                              };
                              break;
                            case 'voided':
                              statusConfig = {
                                color: 'text-gray-500',
                                bgColor: 'bg-gray-100',
                                borderColor: 'border-gray-300',
                                label: 'Voided',
                                icon: '⊘'
                              };
                              break;
                          }
                          
                          return (
                            <div key={payment.id || index} className={`p-3 rounded-lg border backdrop-blur-sm shadow-sm hover:shadow-md transition-all duration-200 ${statusConfig.bgColor} ${statusConfig.borderColor}`}>
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-2 sm:space-y-0">
                                <div className="flex items-center space-x-3">
                                  <div className={`w-3 h-3 rounded-full ${statusConfig.color.replace('text-', 'bg-')} flex-shrink-0 flex items-center justify-center text-xs font-bold text-white`}>
                                    {statusConfig.icon}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className={`font-medium text-sm ${statusConfig.color}`}>
                                      {payment.reference || 'Payment'} - {statusConfig.label}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {new Date(payment.created_at).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                      {payment.method && (
                                        <span className="ml-2 text-gray-500">
                                          • {payment.method.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                        </span>
                                      )}
                                    </div>
                                    {payment.notes && (
                                      <div className="text-xs text-gray-400 mt-1">
                                        {payment.notes}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center justify-between sm:justify-end space-x-3">
                                  <div className={`font-semibold text-base ${statusConfig.color}`}>
                                    ${(payment.amount || 0).toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Product Sale Details Section */}
              <div className="bg-white border-b border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Sale Details</h3>
                
                <div className="space-y-4">
                  {/* Customer Info */}
                  {productSale?.customer && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Customer:</div>
                      <div className="text-base font-medium text-gray-900">
                        {productSale.customer.first_name} {productSale.customer.last_name}
                      </div>
                      {productSale.customer.email && (
                        <div className="text-sm text-gray-600 mt-1">{productSale.customer.email}</div>
                      )}
                      {productSale.customer.phone && (
                        <div className="text-sm text-gray-600">{productSale.customer.phone}</div>
                      )}
                    </div>
                  )}

                  {/* Sale Items */}
                  {productSale?.items && productSale.items.length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Desktop Header */}
                      <div className="hidden sm:block bg-gray-50 p-4">
                        <div className="grid gap-2 font-semibold text-gray-700" style={{ gridTemplateColumns: '3fr 1fr 1fr 1fr' }}>
                          <div>Product</div>
                          <div className="text-center">Quantity</div>
                          <div className="text-right">Price</div>
                          <div className="text-right">Total</div>
                        </div>
                      </div>
                      {/* Mobile Header */}
                      <div className="sm:hidden bg-gradient-to-r from-gray-100 to-gray-50 p-4 border-b border-gray-200">
                        <div className="font-bold text-gray-800 text-base">Products</div>
                      </div>
                      
                      <div className="divide-y sm:divide-y-0">
                        {productSale.items.map((item: any, index: number) => (
                          <div key={item.id || index} className="p-2 sm:p-4">
                            {/* Desktop Grid Layout */}
                            <div className="hidden sm:grid gap-2" style={{ gridTemplateColumns: '3fr 1fr 1fr 1fr' }}>
                              <div className="font-medium">{item.product_name || item.name}</div>
                              <div className="text-center">{item.quantity || 1}</div>
                              <div className="text-right">${(item.unit_price || item.price || 0).toFixed(2)}</div>
                              <div className="text-right font-medium">${(item.total_price || item.total || 0).toFixed(2)}</div>
                            </div>
                            
                            {/* Mobile Card Layout */}
                            <div className="sm:hidden">
                              <div className="bg-gray-50 rounded-lg p-3 space-y-2 shadow-sm">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-base text-gray-900">{item.product_name || item.name}</div>
                                  </div>
                                  <div className="ml-3 text-right">
                                    <div className="font-bold text-lg text-gray-900">${(item.total_price || item.total || 0).toFixed(2)}</div>
                                  </div>
                                </div>
                                <div className="flex justify-end pt-1">
                                  <div className="text-right text-xs text-gray-500">
                                    Qty: {item.quantity || 1} × ${(item.unit_price || item.price || 0).toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Totals */}
                  <div className="flex justify-end">
                    <div className="w-full sm:w-80 lg:w-64">
                      <div className="bg-gray-50 sm:bg-transparent rounded-lg p-3 sm:p-0 space-y-2 text-sm sm:text-base">
                        <div className="flex justify-between py-1">
                          <span className="text-gray-700">Subtotal:</span>
                          <span className="font-semibold">${(productSale?.subtotal_amount || totalAmount).toFixed(2)}</span>
                        </div>
                        {productSale?.discount_amount && productSale.discount_amount > 0 && (
                          <div className="flex justify-between text-red-600 py-1">
                            <span>Discount:</span>
                            <span className="font-semibold">-${(productSale.discount_amount).toFixed(2)}</span>
                          </div>
                        )}
                        {productSale?.tax_amount && productSale.tax_amount > 0 && (
                          <div className="flex justify-between text-gray-600 py-1">
                            <span>Tax ({productSale.tax_rate || 0}%):</span>
                            <span className="font-semibold">${(productSale.tax_amount).toFixed(2)}</span>
                          </div>
                        )}
                        <div className="border-t pt-3 mt-3">
                          <div className="flex justify-between font-bold text-lg sm:text-xl text-gray-900">
                            <span>Total:</span>
                            <span>${totalAmount.toFixed(2)}</span>
                          </div>
                          
                          {completedPayments.length > 0 && (
                            <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200 space-y-1">
                              <div className="flex justify-between text-xs sm:text-sm">
                                <span className="text-gray-600">Amount Paid:</span>
                                <span className="text-green-600 font-medium">${totalPaid.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-xs sm:text-sm">
                                <span className="text-gray-600">Amount Owing:</span>
                                <span className={`font-medium ${balanceOwing > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                  ${balanceOwing.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}

